/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 31 2024
 **/

import { ReactNode, PropsWithChildren, ComponentProps } from "react";
import isEmpty from "lodash-es/isEmpty";

import { UserItem as DesignStuffUserItem } from "@/frameworks/designStuff/compounds";
import { usePopover } from "@/frameworks/core/hooks";
import { GROUP_POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { usePersistUser } from "@/frameworks/core/persistence";
import { MiniProfile } from "../MiniProfile";

type UserItemProps = {
	id?: string;
	userID?: string;
	email?: ReactNode;
	showMiniProfile?: boolean;
	nameCount?: string;
	typeUser?: "group" | "space" | "channel";
	blocked?: boolean;
	groupJoinRequest?: boolean;
} & Omit<
	ComponentProps<typeof DesignStuffUserItem>,
	"src" | "status" | "name" | "email" | "textColor" | "onAvatarHover"
>;

export default function UserItem({ id, email, showMiniProfile, nameCount, ...rest }: PropsWithChildren<UserItemProps>) {
	// id 가 없는 경우 : 서비스에 가입되지 않은 사용자 리스트 출력해야할 때
	if (!id) {
		// name을 String 으로 강제화 시킨것은 타입이 맞지 않기때문
		// id가 존재 할 시 email은 무조건 존재 하는 가정인데 현재 레거시 코드들에서 userItem의 props로 data-email 넘기는 부분들이 있어서 해당 부분 타입 강제로 해결하고 data-email 사용된 부분 리팩토링해야함
		return <DesignStuffUserItem email={email} name={String(email)} {...rest} />;
	}

	const popover = usePopover();
	const user = usePersistUser(id);

	// null return 이유 : api상의 문제 발생시 컴포넌트 랜더링을 시키지 않으려는 의도임. ( 서비스 다운 되지 않기위해 )
	if (!user || isEmpty(user)) {
		return null;
	}

	return (
		<DesignStuffUserItem
			email={email ?? (user?.username || "")}
			name={nameCount ? nameCount : user?.name || ""}
			status={user.activeStatus}
			textColor={`${user?.colorProfile}_50_100` as Avatar.ProfileColor}
			isBlock={user?.blockedByMe || rest.blocked}
			src={user?.profileImagePath}
			onAvatarHover={(ev) => {
				if (!showMiniProfile) return;
				if (!ev.target) return;
				popover.show({
					popoverID: GROUP_POPOVER_ID.AVATAR_MINI_PROFILE,
					uniqueID: user?.id || "",
					content: (
						<div
							onMouseLeave={() => {
								if (
									popover.getPopoverState(
										GROUP_POPOVER_ID.AVATAR_MINI_PROFILE,
										"caller_call_method_option"
									).isOpen
								) {
									return;
								}
								popover.hide(GROUP_POPOVER_ID.AVATAR_MINI_PROFILE);
							}}
							style={{ width: "300px", height: "200px", paddingBottom: "10px" }}
						>
							<MiniProfile userID={user?.id || ""} />
						</div>
					),
					direction: {
						x: "left",
						y: "top"
					},
					position: {
						x: 0,
						y: -155
					},
					anchorEl: ev.target as HTMLElement,
					zIndex: ZIndex.SIDEBAR + 1
				});
			}}
			{...rest}
		/>
	);
}
